import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { NWClient } from '../../client/NWClient';
import { RootState } from '../../store';

export interface BankDetails {
  id: number;
  company: number;
  account_number: string;
  account_name: string;
  swift_code?: string | null;
  country?: string | null;
  currency?: string | null;
}
interface SupplierBankDetailsState {
  supplierBankDetails: BankDetails[] | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: SupplierBankDetailsState = {
  supplierBankDetails: null,
  status: 'idle',
  error: null,
};

export const fetchSupplierBankDetails = createAsyncThunk(
  'supplierBankDetails/fetchSupplierBankDetails',
  async (payload: { supplier: number }) => {
    const response = await NWClient.list(`supplier-bank-details`, {
      supplierId: payload.supplier,
    });
    return response;
  }
);

export const updateSupplierBankDetails = createAsyncThunk(
  'supplierBankDetails/updateSupplierBankDetails',
  async (payload: { supplierId: number; id: number; bankDetails: any }) => {
    const response = await NWClient.put(
      `supplier-bank-details/${payload.supplierId}`,
      payload.id,
      payload.bankDetails,
      true
    );
    return response;
  }
);

export const createSupplierBankDetails = createAsyncThunk(
  'supplierBankDetails/createSupplierBankDetails',
  async (payload: { supplier: number; bankDetails: any }) => {
    const response = await NWClient.post(
      `supplier-bank-details/${payload.supplier}`,
      payload.bankDetails,
      true
    );
    return response;
  }
);

const supplierBankDetailsSlice = createSlice({
  name: 'supplierBankDetails',
  initialState,
  reducers: {
    clearSupplierBankDetails: (state) => {
      state.supplierBankDetails = null;
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchSupplierBankDetails
    builder
      .addCase(fetchSupplierBankDetails.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSupplierBankDetails.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.supplierBankDetails = action.payload as BankDetails[];
      })
      .addCase(fetchSupplierBankDetails.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch supplier bank details';
      });

    // updateSupplierBankDetails
    builder
      .addCase(updateSupplierBankDetails.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateSupplierBankDetails.fulfilled, (state, action) => {
        state.status = 'succeeded';
        Array.isArray(state.supplierBankDetails) &&
          (state.supplierBankDetails[0] = action.payload as BankDetails);
      })
      .addCase(updateSupplierBankDetails.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to update supplier bank details';
      });

    // createSupplierBankDetails
    builder
      .addCase(createSupplierBankDetails.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createSupplierBankDetails.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.supplierBankDetails = [action.payload as BankDetails];
      })
      .addCase(createSupplierBankDetails.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to create supplier bank details';
      });
  },
});

export const selectSupplierBankDetails = (state: RootState) =>
  state.supplierBankDetails.supplierBankDetails;

export const { clearSupplierBankDetails } = supplierBankDetailsSlice.actions;
export default supplierBankDetailsSlice.reducer;
